import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useAuthStore from '../hooks/useAuthStore';

export default function CommunityChat() {
  const navigate = useNavigate();
  const { user, theme } = useAuthStore();
  const isICSE = theme === 'icse';
  const primaryColor = isICSE ? 'icse-teal' : 'isc-coral';
  const secondaryColor = isICSE ? 'icse-cyan' : 'isc-purple';

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const messagesEndRef = useRef(null);

  const chatRooms = [
    { id: 'icse-9', name: 'ICSE Class 9', icon: '🎓', members: 234 },
    { id: 'icse-10', name: 'ICSE Class 10', icon: '📚', members: 312 },
    { id: 'isc-11-science', name: 'ISC Class 11 Science', icon: '⚗️', members: 189 },
    { id: 'isc-11-commerce', name: 'ISC Class 11 Commerce', icon: '💼', members: 156 },
    { id: 'isc-12-science', name: 'ISC Class 12 Science', icon: '🔬', members: 278 },
    { id: 'isc-12-commerce', name: 'ISC Class 12 Commerce', icon: '📊', members: 203 },
  ];

  useEffect(() => {
    if (selectedRoom) {
      // Since we don't have a backend socket server, simulate connection
      // In production, this would connect to: io(import.meta.env.VITE_CHAT_SERVER_URL || 'http://localhost:3001');
      setIsConnected(true);
      
      // Simulate receiving some initial messages
      const mockMessages = [
        { id: 1, userId: 'user1', userName: 'Student A', text: 'Hey everyone! Anyone working on the math assignment?', room: selectedRoom.id, timestamp: new Date() },
        { id: 2, userId: 'user2', userName: 'Student B', text: 'Yes! I can help with chapter 3', room: selectedRoom.id, timestamp: new Date() },
      ];
      setMessages(mockMessages);

      return () => {
        setIsConnected(false);
      };
    }
  }, [selectedRoom]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!message.trim() || !isConnected) return;

    const newMessage = {
      id: Date.now(),
      userId: user.id,
      userName: user.name,
      userStream: user.stream,
      text: message,
      timestamp: new Date().toISOString(),
      room: selectedRoom.id,
    };

    setMessages([...messages, newMessage]);
    setMessage('');
  };

  const handleTyping = () => {
    // Typing indicator not implemented in client-only mode
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const isOwnMessage = (msg) => msg.userId === user.id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => selectedRoom ? setSelectedRoom(null) : navigate('/')}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Community Chat</h1>
              <p className={`text-${primaryColor}-400 text-sm`}>
                {selectedRoom ? selectedRoom.name : 'Select a chat room'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${isConnected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
              <span className="text-sm font-medium">{isConnected ? 'Online' : 'Offline'}</span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Back to Portal
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {!selectedRoom ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {chatRooms.map((room, index) => (
                <motion.div
                  key={room.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedRoom(room)}
                  className="glass-card rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-all border border-white/10"
                >
                  <div className="text-4xl mb-4">{room.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{room.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">{room.members} members</span>
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 flex items-center justify-center text-white`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-2xl border border-white/10 overflow-hidden"
            >
              <div className="h-[600px] flex flex-col">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{selectedRoom.icon}</div>
                    <div>
                      <h2 className="text-white font-bold">{selectedRoom.name}</h2>
                      <p className={`text-${primaryColor}-400 text-sm`}>{selectedRoom.members} members online</p>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isOwnMessage(msg) ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isOwnMessage(msg) ? 'order-2' : 'order-1'}`}>
                        {!isOwnMessage(msg) && (
                          <p className={`text-${primaryColor}-400 text-xs mb-1 ml-2`}>{msg.userName}</p>
                        )}
                        <div
                          className={`p-3 rounded-2xl ${
                            isOwnMessage(msg)
                              ? `bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 text-white`
                              : 'bg-white/10 text-white'
                          }`}
                        >
                          <p className="text-sm">{msg.text}</p>
                        </div>
                        <p className={`text-gray-500 text-xs mt-1 ${isOwnMessage(msg) ? 'text-right mr-2' : 'ml-2'}`}>
                          {formatTime(msg.timestamp)}
                        </p>
                      </div>
                    </motion.div>
                  ))}

                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onInput={handleTyping}
                      placeholder="Type your message..."
                      disabled={!isConnected}
                      className="flex-1 px-4 py-3 bg-white/10 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-${primaryColor}-500 focus:ring-1 focus:ring-${primaryColor}-500 transition-all disabled:opacity-50"
                    />
                    <button
                      type="submit"
                      disabled={!message.trim() || !isConnected}
                      className={`px-6 py-3 bg-gradient-to-r from-${primaryColor}-500 to-${secondaryColor}-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
